/**
 * Turns the signed URL an attachment is read back with into the object path the
 * API accepts when re-submitting it.
 *
 * Comment attachments come back as short-lived CloudFront-signed URLs and never
 * expose their path, but editing a comment replaces the whole attachment list —
 * so the ones being kept have to be sent again as `{ path }`. The backend
 * normalizes a CDN URL by dropping its query, and the path portion is the
 * canonical key, so the pathname is what we send. Relative values (already a
 * path) are returned unchanged apart from a leading slash.
 *
 * Returns null when there is nothing usable, so the caller can drop the
 * attachment instead of sending a value the backend would reject.
 */
export const attachmentUrlToPath = (url: string | undefined): string | null => {
  const trimmed = url?.trim();
  if (!trimmed) {
    return null;
  }

  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const { pathname } = new URL(trimmed);
      return pathname && pathname !== "/" ? pathname : null;
    } catch {
      return null;
    }
  }

  const [path] = trimmed.split(/[?#]/);
  if (!path) {
    return null;
  }
  return path.startsWith("/") ? path : `/${path}`;
};
